import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import ApiClient from "../../../methods/api/apiClient";
import loader from "../../../methods/loader";
import { toast } from "react-toastify";

// ─── Storage keys ─────────────────────────────────────────────────────────────
const SK = {
  STATE: "__aipm_state__",
  PENDING_QUOTE: "__aipm_pending_quote__",
  REDIRECT_AFTER: "__aipm_redirect_after__",
  SHOW_QUOTE_AFTER: "__aipm_show_quote_after__",
};
const TTL = 30 * 60 * 1000;

function storageSave(messages, quantities, inputText, quote) {
  try {
    localStorage.setItem(
      SK.STATE,
      JSON.stringify({ messages, quantities, inputText, ts: Date.now() }),
    );
    if (quote) localStorage.setItem(SK.PENDING_QUOTE, JSON.stringify(quote));
    else localStorage.removeItem(SK.PENDING_QUOTE);
  } catch (e) {
    console.error("[AIModal] save:", e);
  }
}

function storageLoadState() {
  try {
    const raw = localStorage.getItem(SK.STATE);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (!p.ts || Date.now() - p.ts > TTL) {
      storageClearAll();
      return null;
    }
    return p;
  } catch {
    storageClearAll();
    return null;
  }
}

function storageLoadQuote() {
  try {
    const raw = localStorage.getItem(SK.PENDING_QUOTE);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function storageClearAll() {
  [SK.STATE, SK.PENDING_QUOTE, SK.SHOW_QUOTE_AFTER, SK.REDIRECT_AFTER].forEach(
    (k) => localStorage.removeItem(k),
  );
}

// ─── TypingIndicator ──────────────────────────────────────────────────────────
function TypingIndicator() {
  return (
    <div className="flex items-end gap-2 mb-3">
      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#115B9D] to-[#116DAE] flex items-center justify-center flex-shrink-0">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
          <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
        </svg>
      </div>
      <div className="bg-white border border-gray-100 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
        <div className="flex gap-1.5 items-center h-4">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="w-1.5 h-1.5 rounded-full bg-[#116DAE] animate-bounce"
              style={{
                animationDelay: `${i * 0.18}s`,
                animationDuration: "0.85s",
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── ChatMessage wrapper ──────────────────────────────────────────────────────
function ChatMessage({ role, children }) {
  const isAI = role === "ai";
  return (
    <div
      className={`flex items-end gap-2 mb-3 ${isAI ? "" : "flex-row-reverse"}`}
    >
      {isAI && (
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#115B9D] to-[#116DAE] flex items-center justify-center flex-shrink-0 mb-0.5 shadow-md">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
          </svg>
        </div>
      )}
      <div
        className={`max-w-[85%] px-4 py-2.5 shadow-sm text-sm leading-relaxed ${
          isAI
            ? "bg-white border border-gray-100 rounded-2xl rounded-bl-sm text-[#1a1a1a] w-full"
            : "bg-gradient-to-br from-[#115B9D] to-[#116DAE] text-white rounded-2xl rounded-br-sm"
        }`}
      >
        {children}
      </div>
    </div>
  );
}

// ─── ProductCard ──────────────────────────────────────────────────────────────
function ProductCard({
  product,
  quantity,
  onQuantityChange,
  selected,
  onToggleSelect,
  onRequestSingle,
}) {
  return (
    <div
      className={`rounded-xl border-2 transition-all duration-200 overflow-hidden ${
        selected
          ? "border-[#116DAE] bg-[#EBF3FB]"
          : "border-gray-100 bg-gray-50"
      }`}
    >
      {/* Card header with checkbox */}
      <div className="flex items-start gap-2.5 p-3">
        <button
          type="button"
          onClick={onToggleSelect}
          className={`mt-0.5 w-5 h-5 flex-shrink-0 rounded-md border-2 flex items-center justify-center transition-all duration-150 ${
            selected
              ? "bg-[#116DAE] border-[#116DAE]"
              : "bg-white border-gray-300 hover:border-[#116DAE]"
          }`}
        >
          {selected && (
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
              <path
                d="M2 6l3 3 5-5"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-[#1a1a1a] leading-snug">
            {product.title}
          </p>
          {product.model && (
            <p className="text-xs text-gray-400 mt-0.5">
              Model: {product.model}
            </p>
          )}
          {product.description && (
            <p className="text-xs text-gray-500 mt-1.5 leading-relaxed line-clamp-2">
              {product.description}
            </p>
          )}
          {product.keySpecifications?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {product.keySpecifications.slice(0, 3).map((spec, i) => (
                <span
                  key={i}
                  className="text-[10px] bg-white border border-gray-200 text-gray-500 px-2 py-0.5 rounded-full"
                >
                  {spec}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quantity + single quote row */}
      <div className="flex items-center justify-between gap-2 px-3 pb-3">
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-500 font-medium">Qty:</span>
          <div className="flex items-center bg-white border border-[#C3C3C3] rounded-lg overflow-hidden">
            <button
              type="button"
              disabled={quantity <= 1}
              onClick={() => onQuantityChange(Math.max(1, quantity - 1))}
              className="w-6 h-6 flex items-center justify-center text-[#116DAE] font-bold text-base hover:bg-[#EBF3FB] transition-colors disabled:opacity-30 disabled:cursor-not-allowed select-none"
            >
              −
            </button>
            <input
              type="number"
              min={1}
              value={quantity}
              onChange={(e) =>
                onQuantityChange(Math.max(1, parseInt(e.target.value) || 1))
              }
              className="w-9 text-center text-sm font-semibold text-[#1a1a1a] outline-none border-none bg-transparent tabular-nums"
            />
            <button
              type="button"
              onClick={() => onQuantityChange(quantity + 1)}
              className="w-6 h-6 flex items-center justify-center text-[#116DAE] font-bold text-base hover:bg-[#EBF3FB] transition-colors select-none"
            >
              +
            </button>
          </div>
        </div>
        {!selected && (
          <button
            onClick={onRequestSingle}
            className="py-1.5 px-3 bg-gradient-to-br from-[#115B9D] to-[#116DAE] text-white text-xs font-semibold rounded-lg shadow hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 flex items-center gap-1"
          >
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
            Request TO Quote
          </button>
        )}
      </div>
    </div>
  );
}

// ─── MultiProductBlock ────────────────────────────────────────────────────────
function MultiProductBlock({
  msgIdx,
  products,
  quantities,
  setQuantities,
  followUpQuestion,
  onRequestSingle,
  onRequestSelected,
}) {
  const [selected, setSelected] = useState(new Set());

  const toggleSelect = (i) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  };

  const selectedItems = [...selected].map((i) => ({
    product: products[i],
    quantity: quantities[`${msgIdx}_${i}`] || 1,
  }));

  return (
    <div className="mt-3 pt-3 border-t border-gray-100 space-y-2.5">
      <p className="text-[10px] font-semibold text-[#116DAE] uppercase tracking-widest">
        {products.length} Suggested Product{products.length > 1 ? "s" : ""}
      </p>

      <div className="space-y-2">
        {products.map((product, i) => (
          <ProductCard
            key={i}
            product={product}
            quantity={quantities[`${msgIdx}_${i}`] || 1}
            onQuantityChange={(val) =>
              setQuantities((prev) => ({ ...prev, [`${msgIdx}_${i}`]: val }))
            }
            selected={selected.has(i)}
            onToggleSelect={() => toggleSelect(i)}
            onRequestSingle={() =>
              onRequestSingle(product, quantities[`${msgIdx}_${i}`] || 1)
            }
          />
        ))}
      </div>

      {selected.size > 0 && (
        <button
          onClick={() => onRequestSelected(selectedItems)}
          className="w-full py-2 px-4 bg-gradient-to-br from-[#115B9D] to-[#116DAE] text-white text-xs font-semibold rounded-xl shadow hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-1.5"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
          </svg>
          Request Quote for {selected.size} Selected
        </button>
      )}

      {followUpQuestion && (
        <p className="text-[11px] text-gray-500 text-center leading-snug bg-gray-50 rounded-lg px-3 py-2">
          💬 {followUpQuestion}
        </p>
      )}
    </div>
  );
}

function CustomProductQuoteBlock({ customProduct, onRequestQuote }) {
  const [qty, setQty] = useState(1);
  const [desc, setDesc] = useState("");

  useEffect(() => {
    setDesc("");
  }, [customProduct]);

  if (!customProduct) return null;

  const handleRequestQuote = () => {
    const finalDesc = desc.trim() !== "" ? desc : (customProduct?.description || "");
    onRequestQuote({ ...customProduct, description: finalDesc }, qty);
  };

  return (
    <div className="mt-3 pt-3 border-t border-dashed border-amber-200">
      <div className="bg-amber-50 border border-amber-200 rounded-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-2 px-3 py-2.5 bg-amber-100 border-b border-amber-200">
          <div className="w-6 h-6 rounded-lg bg-amber-200 flex items-center justify-center flex-shrink-0">
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#92400e"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
          </div>
          <div>
            <p className="text-[10px] font-bold text-amber-900 uppercase tracking-widest leading-tight">
              Not finding what you need?
            </p>
            <p className="text-[10px] text-amber-600 leading-tight mt-0.5">
              We'll source it for you — just request a quote below.
            </p>
          </div>
        </div>

        {/* Product info */}
        <div className="px-3 py-2.5 space-y-1">
          <p className="text-[10px] font-semibold text-amber-700 uppercase tracking-widest">
            Based on your request
          </p>
          <p className="text-sm font-bold text-[#1a1a1a] leading-snug">
            {customProduct.title}
          </p>
          <p className="text-[10px] font-semibold text-gray-800 uppercase tracking-widest mt-3 mb-1">
            Detail Description (Optional)
          </p>
          <textarea
            className="w-full text-xs text-gray-700 leading-relaxed bg-white border border-black rounded-md p-2 mt-1 outline-none focus:border-black focus:bg-white focus:ring-1 focus:ring-black resize-y min-h-[70px]"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="Additional requirements or description..."
          />
        </div>

        {/* Divider */}
        <div className="mx-3 border-t border-amber-200" />

        {/* Quantity + CTA row */}
        <div className="flex items-center gap-3 px-3 py-2.5">
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-xs text-gray-600 font-medium">Qty:</span>
            <div className="flex items-center bg-white border border-amber-300 rounded-lg overflow-hidden">
              <button
                type="button"
                disabled={qty <= 1}
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="w-6 h-6 flex items-center justify-center text-amber-700 font-bold text-base hover:bg-amber-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed select-none"
              >
                −
              </button>
              <input
                type="number"
                min={1}
                value={qty}
                onChange={(e) =>
                  setQty(Math.max(1, parseInt(e.target.value) || 1))
                }
                className="w-9 text-center text-sm font-semibold text-[#1a1a1a] outline-none border-none bg-transparent tabular-nums"
              />
              <button
                type="button"
                onClick={() => setQty((q) => q + 1)}
                className="w-6 h-6 flex items-center justify-center text-amber-700 font-bold text-base hover:bg-amber-50 transition-colors select-none"
              >
                +
              </button>
            </div>
          </div>

          <button
            onClick={handleRequestQuote}
            className="flex-1 py-1.5 px-3 bg-gradient-to-br from-[#115B9D] to-[#116DAE] text-white text-xs font-semibold rounded-lg shadow hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-1.5"
          >
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
            Request to Quote
          </button>
        </div>

        {/* Footer note */}
        <p className="text-[10px] text-amber-600 text-center pb-2.5 px-3 leading-snug">
          Our team will review and get back to you with pricing &amp;
          availability.
        </p>
      </div>
    </div>
  );
}

// ─── GuestOrSignupModal ───────────────────────────────────────────────────────
function GuestOrSignupModal({ show, onClose, onSelectGuest, onSelectSignup }) {
  if (!show) return null;
  return (
    <div className="fixed inset-0 z-[10010] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
      <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full">
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full bg-[#EBF3FB] flex items-center justify-center mx-auto mb-4">
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#116DAE"
              strokeWidth="1.8"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <h3 className="text-lg font-bold text-[#1a1a1a] mb-2">
            Request a Quote
          </h3>
          <p className="text-sm text-gray-500">
            Would you like to continue as a guest or sign up for an account?
          </p>
        </div>
        <div className="space-y-3">
          <button
            onClick={onSelectGuest}
            className="w-full py-3 px-4 bg-white border-2 border-[#116DAE] text-[#116DAE] font-semibold rounded-xl hover:bg-[#EBF3FB] transition-all duration-200 flex items-center justify-center gap-2"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            Continue as Guest
          </button>
          <button
            onClick={onSelectSignup}
            className="w-full py-3 px-4 bg-gradient-to-br from-[#115B9D] to-[#116DAE] text-white font-semibold rounded-xl hover:scale-[1.02] transition-all duration-200 flex items-center justify-center gap-2"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            Sign Up / Log In
          </button>
        </div>
        <button
          onClick={onClose}
          className="w-full mt-4 py-2 text-gray-400 text-sm hover:text-gray-600 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─── QuoteModal ───────────────────────────────────────────────────────────────
function QuoteModal({ show, onClose, onSubmit, lineItems, detail }) {
  const user = useSelector((s) => s.user);
  const [sameAsBilling, setSameAsBilling] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    customer_name: "",
    email: "",
    billingAddress: {
      bill_line1: "",
      bill_line2: "",
      bill_city: "",
      bill_country: "",
      bill_postal_code: "",
    },
    shippingAddress: {
      ship_line1: "",
      ship_line2: "",
      ship_city: "",
      ship_country: "",
      ship_postal_code: "",
    },
  });

  useEffect(() => {
    if (!show) return;
    setForm({
      customer_name: user?.fullName || detail?.customer_name || "",
      email: user?.email || detail?.email || "",
      billingAddress: {
        bill_line1: detail?.billingAddress?.bill_line1 || "",
        bill_line2: detail?.billingAddress?.bill_line2 || "",
        bill_city: detail?.billingAddress?.bill_city || "",
        bill_country: detail?.billingAddress?.bill_country || "",
        bill_postal_code: detail?.billingAddress?.bill_postal_code || "",
      },
      shippingAddress: {
        ship_line1: detail?.shippingAddress?.ship_line1 || "",
        ship_line2: detail?.shippingAddress?.ship_line2 || "",
        ship_city: detail?.shippingAddress?.ship_city || "",
        ship_country: detail?.shippingAddress?.ship_country || "",
        ship_postal_code: detail?.shippingAddress?.ship_postal_code || "",
      },
    });
    setSameAsBilling(false);
    setIsSubmitting(false);
  }, [show, detail, user]);

  const handleBilling = (field, value) => {
    setForm((prev) => {
      const billing = { ...prev.billingAddress, [field]: value };
      const shipping = sameAsBilling
        ? { ...prev.shippingAddress, [field.replace("bill_", "ship_")]: value }
        : prev.shippingAddress;
      return { ...prev, billingAddress: billing, shippingAddress: shipping };
    });
  };

  const handleSameAsBilling = (checked) => {
    setSameAsBilling(checked);
    if (checked) {
      setForm((prev) => ({
        ...prev,
        shippingAddress: {
          ship_line1: prev.billingAddress.bill_line1,
          ship_line2: prev.billingAddress.bill_line2,
          ship_city: prev.billingAddress.bill_city,
          ship_country: prev.billingAddress.bill_country,
          ship_postal_code: prev.billingAddress.bill_postal_code,
        },
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    loader(true);
    try {
      const payload = {
        ...form,
        line_items: lineItems?.map((item) => ({
          item_name: item?.product?.title || item?.item_name || "",
          description: item?.product?.description || item?.description || "",
          qty: item.quantity || 1,
          rate: null,
          amount: null,
          image: item?.product?.image || "",
          manufacturer: item?.product?.manufacturer || "",
          model: item?.product?.model || "",
          modelNumber: item?.product?.modelNumber || [],
          equipmentType: item?.product?.equipmentType || "",
          keySpecifications: item?.product?.keySpecifications || [],
        })),
      };
      const res = await ApiClient.post("request-quote/add", payload);
      if (
        res &&
        (res.success === true ||
          res.status === "success" ||
          res.success !== false)
      ) {
        toast.success("Quote request submitted successfully!");
        onSubmit(form);
      } else {
        toast.error(res?.message || res?.error || "Failed to request quote");
        setIsSubmitting(false);
      }
    } catch (err) {
      if (err?.message?.includes("memory fallback")) {
        toast.success("Quote request submitted successfully!");
        onSubmit(form);
      } else {
        toast.error(
          err?.response?.data?.message ||
            "An error occurred while requesting quote",
        );
        setIsSubmitting(false);
      }
    } finally {
      loader(false);
    }
  };

  if (!show) return null;

  const inp =
    "w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-[#116DAE]";
  const dis = "disabled:bg-gray-50 disabled:cursor-not-allowed";

  return (
    <div className="fixed inset-0 z-[10008] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4 overflow-y-auto py-10">
      <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-2xl w-full my-auto">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-xl font-bold text-[#1a1a1a]">
              Request Quote Details
            </h3>
            {lineItems?.length > 1 && (
              <p className="text-xs text-[#116DAE] font-medium mt-0.5">
                {lineItems.length} products selected
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Customer Name *
              </label>
              <input
                required
                type="text"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#116DAE] outline-none capitalize"
                value={form.customer_name}
                disabled={!!user?._id || isSubmitting}
                onChange={(e) =>
                  setForm((p) => ({ ...p, customer_name: e.target.value }))
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <input
                required
                type="email"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#116DAE] outline-none"
                value={form.email}
                disabled={!!user?._id || isSubmitting}
                onChange={(e) =>
                  setForm((p) => ({ ...p, email: e.target.value }))
                }
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-3">
              <h4 className="font-semibold text-gray-900 border-b pb-2">
                Billing Address
              </h4>
              <input
                required
                type="text"
                placeholder="Address Line 1 *"
                className={inp}
                value={form.billingAddress.bill_line1}
                disabled={isSubmitting}
                onChange={(e) => handleBilling("bill_line1", e.target.value)}
              />
              <input
                type="text"
                placeholder="Address Line 2"
                className={inp}
                value={form.billingAddress.bill_line2}
                disabled={isSubmitting}
                onChange={(e) => handleBilling("bill_line2", e.target.value)}
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  required
                  type="text"
                  placeholder="City *"
                  className={inp}
                  value={form.billingAddress.bill_city}
                  disabled={isSubmitting}
                  onChange={(e) => handleBilling("bill_city", e.target.value)}
                />
                <input
                  required
                  type="text"
                  placeholder="Postal Code *"
                  className={inp}
                  value={form.billingAddress.bill_postal_code}
                  disabled={isSubmitting}
                  onChange={(e) =>
                    handleBilling("bill_postal_code", e.target.value)
                  }
                />
              </div>
              <input
                required
                type="text"
                placeholder="Country *"
                className={inp}
                value={form.billingAddress.bill_country}
                disabled={isSubmitting}
                onChange={(e) => handleBilling("bill_country", e.target.value)}
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between border-b pb-2">
                <h4 className="font-semibold text-gray-900">
                  Shipping Address
                </h4>
                <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sameAsBilling}
                    className="rounded text-[#116DAE]"
                    disabled={isSubmitting}
                    onChange={(e) => handleSameAsBilling(e.target.checked)}
                  />
                  Same as Billing
                </label>
              </div>
              <input
                required
                type="text"
                placeholder="Address Line 1 *"
                className={`${inp} ${dis}`}
                disabled={sameAsBilling || isSubmitting}
                value={form.shippingAddress.ship_line1}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    shippingAddress: {
                      ...p.shippingAddress,
                      ship_line1: e.target.value,
                    },
                  }))
                }
              />
              <input
                type="text"
                placeholder="Address Line 2"
                className={`${inp} ${dis}`}
                disabled={sameAsBilling || isSubmitting}
                value={form.shippingAddress.ship_line2}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    shippingAddress: {
                      ...p.shippingAddress,
                      ship_line2: e.target.value,
                    },
                  }))
                }
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  required
                  type="text"
                  placeholder="City *"
                  className={`${inp} ${dis}`}
                  disabled={sameAsBilling || isSubmitting}
                  value={form.shippingAddress.ship_city}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      shippingAddress: {
                        ...p.shippingAddress,
                        ship_city: e.target.value,
                      },
                    }))
                  }
                />
                <input
                  required
                  type="text"
                  placeholder="Postal Code *"
                  className={`${inp} ${dis}`}
                  disabled={sameAsBilling || isSubmitting}
                  value={form.shippingAddress.ship_postal_code}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      shippingAddress: {
                        ...p.shippingAddress,
                        ship_postal_code: e.target.value,
                      },
                    }))
                  }
                />
              </div>
              <input
                required
                type="text"
                placeholder="Country *"
                className={`${inp} ${dis}`}
                disabled={sameAsBilling || isSubmitting}
                value={form.shippingAddress.ship_country}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    shippingAddress: {
                      ...p.shippingAddress,
                      ship_country: e.target.value,
                    },
                  }))
                }
              />
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 border border-gray-200 text-gray-600 font-semibold rounded-full hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 bg-gradient-to-br from-[#115B9D] to-[#116DAE] text-white font-semibold rounded-full shadow hover:scale-[1.02] active:scale-[0.98] transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Submitting..." : "Submit Quote Request"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AIProductModal({
  isOpen,
  onClose,
  onRequestQuote,
  detail,
}) {
  const hostname = window.location.hostname;
  // const isLogisticsHost = hostname === "logistics.charlessmithassociates.com";
  const isLogisticsHost = "localhost";
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((s) => s.user);
  const bottomRef = useRef(null);

  const FRESH = [
    {
      role: "ai",
      text: "Hi! 👋 Enter a manufacturer or model number—or just describe the item if you don't have those details.",
      products: null,
      followUpQuestion: null,
      customProduct: null,
    },
  ];

  const [messages, setMessages] = useState(FRESH);
  const [quantities, setQuantities] = useState({});
  const [inputText, setInputText] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingQuote, setPendingQuote] = useState(null);
  const [showGuestSignupModal, setShowGuestSignupModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [aiModalReady, setAiModalReady] = useState(false);

  const isLoggedIn = !!user?._id;

  const snap = useRef({
    messages: FRESH,
    quantities: {},
    inputText: "",
    quote: null,
  });
  snap.current.messages = messages;
  snap.current.quantities = quantities;
  snap.current.inputText = inputText;
  snap.current.quote = pendingQuote;

  const saveNow = useCallback(() => {
    storageSave(
      snap.current.messages,
      snap.current.quantities,
      snap.current.inputText,
      snap.current.quote,
    );
  }, []);

  // ── On open: restore or start fresh ─────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const returning = localStorage.getItem(SK.SHOW_QUOTE_AFTER) === "true";
    if (returning) {
      localStorage.removeItem(SK.SHOW_QUOTE_AFTER);
      localStorage.removeItem(SK.REDIRECT_AFTER);
    }
    const saved = storageLoadState();
    const quote = storageLoadQuote();
    if (saved) {
      setMessages(saved.messages || FRESH);
      setQuantities(saved.quantities || {});
      setInputText(saved.inputText || "");
    } else {
      setMessages(FRESH);
      setQuantities({});
      setInputText("");
    }
    if (quote) {
      setPendingQuote(quote);
      if (returning) {
        setAiModalReady(true);
        setShowQuoteModal(false);
        setTimeout(() => setShowQuoteModal(true), 300);
      } else {
        setAiModalReady(true);
      }
    } else {
      setPendingQuote(null);
      setAiModalReady(true);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setShowQuoteModal(false);
      setShowGuestSignupModal(false);
      setAiModalReady(false);
    }
  }, [isOpen]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  if (!isOpen) return null;

  const generateMongoLikeId = () => {
    const timestamp = Math.floor(Date.now() / 1000).toString(16);

    const random = Array.from({ length: 16 }, () =>
      Math.floor(Math.random() * 16).toString(16),
    ).join("");

    return (timestamp + random).slice(0, 24);
  };

  // get or create visitor id
  const getVisitorId = () => {
    const SESSION_KEY = "ai_visitor_id";

    let visitorId = sessionStorage.getItem(SESSION_KEY);

    if (!visitorId) {
      visitorId = generateMongoLikeId();
      sessionStorage.setItem(SESSION_KEY, visitorId);
    }

    return visitorId;
  };

  // ── Send message ─────────────────────────────────────────────────────────────
  const handleSend = async () => {
    const trimmed = inputText.trim();
    if (!trimmed || loading) return;

    setMessages((prev) => [
      ...prev,
      {
        role: "user",
        text: trimmed,
        products: null,
        followUpQuestion: null,
        customProduct: null,
      },
    ]);
    setInputText("");
    setLoading(true);

    const type = isLogisticsHost ? "generalProductAI" : "industrialProductAI";

    try {
      const res = await ApiClient.post("question/ai-generative", {
        question: trimmed,
        visitorId: getVisitorId(),
        type: type,
      });

      const data = res?.data;
      const products =
        data?.products || (data && !data.products ? [data] : null);
      const followUpQuestion = data?.followUpQuestion || null;
      // Pull customProduct from response (only used on logistics host)
      const customProduct = isLogisticsHost
        ? data?.customProduct || null
        : null;

      if (products?.length) {
        const aiText =
          data?.message || `Here's a product we found: ${products[0].title}`;
        setMessages((prev) => {
          const idx = prev.length;
          setQuantities((q) => {
            const next = { ...q };
            products.forEach((_, i) => {
              next[`${idx}_${i}`] = 1;
            });
            return next;
          });
          return [
            ...prev,
            {
              role: "ai",
              text: aiText,
              products,
              followUpQuestion,
              customProduct,
            },
          ];
        });
      } else {
        const aiText =
          res?.data?.message ||
          "I couldn't find a matching product. Could you provide more details?";
        setMessages((prev) => [
          ...prev,
          {
            role: "ai",
            text: aiText,
            products: null,
            followUpQuestion: followUpQuestion,
            customProduct,
          },
        ]);
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: "ai",
          text: "Our AI assistant is temporarily unavailable. Please try again shortly.",
          products: null,
          followUpQuestion: null,
          customProduct: null,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── Quote flow ────────────────────────────────────────────────────────────────
  const initiateQuote = (lineItems) => {
    const quote = { lineItems };
    setPendingQuote(quote);
    snap.current.quote = quote;
    if (!isLoggedIn) {
      setShowGuestSignupModal(true);
    } else {
      setShowQuoteModal(true);
    }
  };

  const handleGuestSelect = () => {
    setShowGuestSignupModal(false);
    setShowQuoteModal(true);
  };

  const handleSignupSelect = () => {
    saveNow();
    localStorage.setItem(SK.SHOW_QUOTE_AFTER, "true");
    localStorage.setItem(SK.REDIRECT_AFTER, location.pathname);
    setShowGuestSignupModal(false);
    navigate("/signup");
  };

  const handleQuoteSubmit = (formData) => {
    if (!pendingQuote) return;
    const line_items = pendingQuote.lineItems.map(({ product, quantity }) => ({
      item_name: product?.title || "Custom Product",
      description: product?.description || "",
      qty: quantity,
      rate: null,
      amount: null,
    }));
    if (onRequestQuote) onRequestQuote({ ...formData, line_items });
    setShowQuoteModal(false);
    setPendingQuote(null);
    storageClearAll();
    onClose();
  };

  const handleCloseAIModal = () => {
    storageClearAll();
    onClose();
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <>
      {!showQuoteModal && (
        <div
          className="fixed inset-0 z-[10005] bg-black/40 backdrop-blur-sm"
          onClick={handleCloseAIModal}
        />
      )}

      <div className="fixed inset-0 z-[10006] flex items-center justify-center px-4 pointer-events-none">
        <div
          className="pointer-events-auto bg-[#F4F6FB] rounded-2xl shadow-2xl flex flex-col w-full max-w-lg overflow-hidden h-[calc(100vh_-_55px)] max-h-[600px]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-br from-[#115B9D] to-[#116DAE] flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center shadow-inner">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                  <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
                </svg>
              </div>
              <div>
                <p className="text-white font-semibold text-sm leading-tight">
                  Product Assistant
                </p>
                <p className="text-white/70 text-xs">
                  Can't find what you need? Ask me!
                </p>
              </div>
            </div>
            <button
              onClick={handleCloseAIModal}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 text-white transition-all active:scale-90"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 overscroll-contain">
            {messages.map((msg, idx) => (
              <ChatMessage key={idx} role={msg.role}>
                <p className="text-sm leading-relaxed">{msg.text}</p>

                {/* Follow-up question (no products) */}
                {msg.role === "ai" &&
                  !msg.products?.length &&
                  msg.followUpQuestion && (
                    <div className="mt-3">
                      <div className="bg-gray-50 border border-gray-200 rounded-2xl px-4 py-3 max-w-[85%]">
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">
                          Follow-up
                        </p>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {msg.followUpQuestion}
                        </p>
                      </div>
                    </div>
                  )}

                {/* Multi-product block */}
                {msg.role === "ai" && msg.products?.length > 0 && (
                  <MultiProductBlock
                    msgIdx={idx}
                    products={msg.products}
                    quantities={quantities}
                    setQuantities={setQuantities}
                    followUpQuestion={msg.followUpQuestion}
                    onRequestSingle={(product, quantity) =>
                      initiateQuote([{ product, quantity }])
                    }
                    onRequestSelected={(selectedItems) =>
                      initiateQuote(selectedItems)
                    }
                  />
                )}

                {/* Fallback: old single-product shape */}
                {msg.role === "ai" && msg.product && !msg.products && (
                  <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
                    <div className="bg-[#EBF3FB] rounded-xl px-3 py-2.5">
                      <p className="text-[10px] font-semibold text-[#116DAE] uppercase tracking-widest mb-1">
                        Suggested Product
                      </p>
                      {msg.product.title && (
                        <p className="text-sm font-bold text-[#1a1a1a] leading-snug">
                          {msg.product.title}
                        </p>
                      )}
                      {msg.product.description && (
                        <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                          {msg.product.description}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 font-medium flex-shrink-0">
                        Quantity:
                      </span>
                      <div className="flex items-center bg-white border border-[#C3C3C3] rounded-lg overflow-hidden">
                        <button
                          type="button"
                          disabled={(quantities[idx] || 1) <= 1}
                          onClick={() =>
                            setQuantities((p) => ({
                              ...p,
                              [idx]: Math.max(1, (p[idx] || 1) - 1),
                            }))
                          }
                          className="w-7 h-7 flex items-center justify-center text-[#116DAE] font-bold text-base hover:bg-[#EBF3FB] transition-colors disabled:opacity-30 disabled:cursor-not-allowed select-none"
                        >
                          −
                        </button>
                        <input
                          type="number"
                          min={1}
                          value={quantities[idx] || 1}
                          onChange={(e) =>
                            setQuantities((p) => ({
                              ...p,
                              [idx]: Math.max(1, parseInt(e.target.value) || 1),
                            }))
                          }
                          className="w-10 text-center text-sm font-semibold text-[#1a1a1a] outline-none border-none bg-transparent tabular-nums"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setQuantities((p) => ({
                              ...p,
                              [idx]: (p[idx] || 1) + 1,
                            }))
                          }
                          className="w-7 h-7 flex items-center justify-center text-[#116DAE] font-bold text-base hover:bg-[#EBF3FB] transition-colors select-none"
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={() =>
                        initiateQuote([
                          {
                            product: msg.product,
                            quantity: quantities[idx] || 1,
                          },
                        ])
                      }
                      className="w-full py-2 px-4 bg-gradient-to-br from-[#115B9D] to-[#116DAE] text-white text-xs font-semibold rounded-xl shadow hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-1.5"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                      </svg>
                      Request a Quote
                    </button>
                  </div>
                )}

                {msg.role === "ai" &&
                  isLogisticsHost &&
                  msg.customProduct &&
                  msg.customProduct?.title && (
                    <CustomProductQuoteBlock
                      customProduct={msg.customProduct}
                      onRequestQuote={(product, quantity) =>
                        initiateQuote([{ product, quantity }])
                      }
                    />
                  )}
              </ChatMessage>
            ))}
            {loading && <TypingIndicator />}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="px-4 pb-4 pt-2 flex-shrink-0 bg-[#F4F6FB]">
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-2xl px-3 py-2 shadow-sm focus-within:border-[#116DAE] focus-within:ring-2 focus-within:ring-[#116DAE]/10 transition-all">
              <textarea
                rows={2}
                className="flex-1 resize-none text-sm text-[#1a1a1a] placeholder-gray-400 outline-none bg-transparent leading-relaxed"
                placeholder="E.g. commercial soda system, beverage pumps, dispensers"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={loading}
              />
              <button
                onClick={handleSend}
                disabled={loading || !inputText.trim()}
                className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-xl bg-gradient-to-br from-[#115B9D] to-[#116DAE] text-white shadow hover:scale-110 active:scale-95 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>
            </div>
            <p className="text-[11px] text-gray-400 text-center mt-1.5">
              <span className="font-medium">Enter</span> to send ·{" "}
              <span className="font-medium">Shift+Enter</span> for new line
            </p>
          </div>
        </div>
      </div>

      <GuestOrSignupModal
        show={showGuestSignupModal}
        onClose={() => {
          setShowGuestSignupModal(false);
          setPendingQuote(null);
        }}
        onSelectGuest={handleGuestSelect}
        onSelectSignup={handleSignupSelect}
      />

      <QuoteModal
        show={showQuoteModal}
        onClose={() => setShowQuoteModal(false)}
        onSubmit={handleQuoteSubmit}
        lineItems={pendingQuote?.lineItems || []}
        detail={detail}
      />
    </>
  );
}
